import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

type PageItem = number | 'ellipsis';

@Component({
  selector: 'app-treelist-pager',
  templateUrl: './treelist-pager.component.html',
  styleUrls: ['./treelist-pager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreelistPagerComponent {
  @Input() total: number = 0;
  @Input() skip: number = 0;
  @Input() pageSize: number = 20;
  @Input() pageSizes: number[] = [10, 20, 50, 100];
  @Output() pageChange = new EventEmitter<{ skip: number; take: number }>();

  get currentPage(): number {
    return Math.floor(this.skip / this.pageSize) + 1;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get rangeStart(): number {
    return this.total === 0 ? 0 : this.skip + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.skip + this.pageSize, this.total);
  }

  getPageNumbers(): PageItem[] {
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const windowStart = Math.max(2, current - 1);
    const windowEnd = Math.min(total - 1, current + 1);

    const pages: PageItem[] = [1];

    if (windowStart > 2) {
      pages.push('ellipsis');
    }

    for (let p = windowStart; p <= windowEnd; p++) {
      pages.push(p);
    }

    if (windowEnd < total - 1) {
      pages.push('ellipsis');
    }

    pages.push(total);
    return pages;
  }

  goToFirst(): void {
    this.emit(0);
  }

  goToPrev(): void {
    this.emit(Math.max(0, this.skip - this.pageSize));
  }

  goToPage(n: number): void {
    this.emit((n - 1) * this.pageSize);
  }

  goToNext(): void {
    this.emit(Math.min((this.totalPages - 1) * this.pageSize, this.skip + this.pageSize));
  }

  goToLast(): void {
    this.emit((this.totalPages - 1) * this.pageSize);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.emit(0);
  }

  private emit(newSkip: number): void {
    this.pageChange.emit({ skip: newSkip, take: this.pageSize });
  }
}